import { CSSProperties, Ref, Suspense, lazy, useEffect, useState } from 'react';
import { Play } from '@phosphor-icons/react';
import { useVideoViewerModal } from '@/hooks/useVideoViewerModal';
import type { VideoPlaybackProgress } from '@/services/videoPlayback';
import { useVideoThumbnail } from '@/hooks/getVideoThumnail';
import { getVideoById } from '@/services/videos';
import { Video, VideoSource, WatchState } from '@/types/Video';
import type { BlockBodyAnchor } from '../blockBodyAnchor';
import { hapticsImpactLight } from '@/utils/haptics';
import { getString } from '@/utils/chatUtils/chatDefaultUtils';
import type { VideoPlayerControls } from '@/components/ora/Videos/VideoPlayer';

const VideoPlayer = lazy(() => import('@/components/ora/Videos/VideoPlayer'));

// When a video tool block is engaged it becomes the chat's active anchor — the
// same mechanism vignette/flashcard tool blocks use — so the AI references it and
// the "Asking about" indicator shows it. `title` is carried for that label; the
// backend keys the live-question lookup off `id`. A chat video result may carry
// only an mp4Url with no id; without a real id the backend can't resolve it, so
// we emit no anchor (like the watch_video pill handler) rather than send an
// empty-id anchor that would override a valid content anchor.
const buildVideoAnchor = (video: Video): BlockBodyAnchor =>
  video.id
    ? {
        type: 'video',
        id: video.id,
        title: video.title,
        video: { timestamp_sec: null },
      }
    : null;

const reportVideoAnchor = (
  video: Video,
  onAnchorChange?: (anchor: BlockBodyAnchor) => void
) => {
  const anchor = buildVideoAnchor(video);
  if (anchor) onAnchorChange?.(anchor);
};

type VideoBlockBodyProps = {
  item?: Record<string, unknown>;
  // 'inline' = render the player inline in the thread (main AI chat); 'card' =
  // keep the compact thumbnail card that opens the video viewer modal on click
  // (AI drawer, and the narrow document-viewer chat where an inline player is
  // too cramped).
  videoDisplay?: 'inline' | 'card';
  // Second to open the player at, when the caller knows where the answer is
  // (the companion's `show_video` serve tool carries one). Undefined plays from
  // the start, which is what every existing caller does.
  startSecond?: number;
  // The study set this video was served in (ORA-677). Rides on the player's
  // progress and completion writes so watching it marks the set's item done.
  studySetId?: string;
  // What the inline player just wrote, so a study set's rail can dim the tile
  // and fill its bar as the video plays (ORA-686). The card variant opens the
  // viewer modal instead of mounting a player, so it reports nothing.
  onPlaybackProgress?: (progress: VideoPlaybackProgress) => void;
  onAnchorChange?: (anchor: BlockBodyAnchor) => void;
  // The inline player's imperative controls and its live-question observer,
  // for the course video step's Raise Your Hand (ORA-1055). The card variant
  // mounts no player, so neither is used there.
  playerControlRef?: Ref<VideoPlayerControls>;
  onLiveQuestionOpenChange?: (isOpen: boolean) => void;
  // A course video step: questions beside the video, and a word when one is
  // answered (see VideoPlayer).
  liveQuestionPanelPlacement?: 'auto' | 'beside';
  onLiveQuestionAnswered?: () => void;
};

const normalizeVideoHit = (item: Record<string, unknown>): Video | null => {
  const id = getString(item.id);
  const mp4Url = getString(item.mp4Url);
  const thumbnailUrl = getString(item.thumbnailUrl);
  const now = new Date();

  if (!id && !mp4Url) return null;

  return {
    id,
    title: getString(item.title) || 'Untitled video',
    description: getString(item.description),
    caption: getString(item.caption),
    mp4Url,
    webmUrl: getString(item.webmUrl),
    thumbnailUrl,
    topics: [],
    citationUrl: getString(item.citationUrl),
    citationAuthor: getString(item.citationAuthor),
    citationLicense: getString(item.citationLicense),
    source: VideoSource.ORA,
    versions: [
      {
        id: getString(item.version_id) || `${id || 'chat-video'}-version`,
        mp4Url,
        webmUrl: getString(item.webmUrl),
        withCam: false,
      },
    ],
    createdAt: now,
    updatedAt: now,
    watchState: WatchState.NOT_STARTED,
    videoTabVisibility: true,
  };
};

type ChatVideoCardProps = {
  title: string;
  thumbnailUrl: string;
  description?: string;
  disabled?: boolean;
  isOpening?: boolean;
  error?: string | null;
  errorId?: string;
  onClick?: () => void;
};

// Compact thumbnail card. Used by the drawer variant (opens the modal on click)
// and as the inline variant's loading / error-fallback state.
const ChatVideoCard = ({
  title,
  thumbnailUrl,
  description,
  disabled,
  isOpening,
  error,
  errorId,
  onClick,
}: ChatVideoCardProps) => (
  <div className="chat-ui_video">
    <button
      className="video-card session-summary_videos_video-card chat-ui_video-card"
      disabled={disabled}
      onClick={onClick}
      style={
        {
          '--video-thumbnail': `url("${thumbnailUrl}")`,
        } as CSSProperties
      }
      type="button"
    >
      <div className="play-button">
        <Play weight="fill" />
      </div>
      <div className="video-info">
        <h3>{title}</h3>
      </div>
    </button>
    {description ? <p>{description}</p> : null}
    {isOpening ? <small>Opening video...</small> : null}
    {error ? (
      <div className="chat-ui_missing">
        {error}
        {errorId ? ` Result ID: ${errorId}.` : ''}
      </div>
    ) : null}
  </div>
);

type InlineChatVideoProps = {
  item: Record<string, unknown>;
  id: string;
  title: string;
  thumbnailUrl: string;
  description?: string;
  startSecond?: number;
  studySetId?: string;
  onPlaybackProgress?: (progress: VideoPlaybackProgress) => void;
  onAnchorChange?: (anchor: BlockBodyAnchor) => void;
  playerControlRef?: Ref<VideoPlayerControls>;
  onLiveQuestionOpenChange?: (isOpen: boolean) => void;
  liveQuestionPanelPlacement?: 'auto' | 'beside';
  onLiveQuestionAnswered?: () => void;
};

// Fetches the full video (versions + live questions) and renders the player
// inline in the chat thread. VideoPlayer enforces its own access gate on play
// and mounting is side-effect free (no view/analytics until play), so an idle
// inline player never consumes a user's video limit.
const InlineChatVideo = ({
  item,
  id,
  title,
  thumbnailUrl,
  description,
  startSecond,
  studySetId,
  onPlaybackProgress,
  onAnchorChange,
  playerControlRef,
  onLiveQuestionOpenChange,
  liveQuestionPanelPlacement,
  onLiveQuestionAnswered,
}: InlineChatVideoProps) => {
  const { open: openVideoViewer } = useVideoViewerModal();
  const [video, setVideo] = useState<Video | null>(null);

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading'
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setStatus('loading');
      try {
        const fetched = id ? await getVideoById(id) : normalizeVideoHit(item);
        if (cancelled) return;
        if (fetched) {
          setVideo(fetched);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      } catch {
        if (cancelled) return;
        // Fall back to whatever the tool result carried so the user can still
        // watch when the lookup fails.
        const fallback = normalizeVideoHit(item);
        if (fallback) {
          setVideo(fallback);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
    // `item` is stable for a given tool block; key the fetch on the video id.
  }, [id]);

  if (status === 'ready' && video) {
    // The description + credit are rendered by VideoPlayer's own info UI, so we
    // don't repeat them here (the drawer card still shows its own description).
    return (
      <div className="chat-ui_video">
        <Suspense
          fallback={
            <ChatVideoCard title={title} thumbnailUrl={thumbnailUrl} disabled />
          }
        >
          <VideoPlayer
            video={video}
            getThumbnail={() => video.thumbnailUrl || thumbnailUrl}
            constrainPanelToContainer
            liveQuestionPanelPlacement={liveQuestionPanelPlacement}
            onLiveQuestionAnswered={
              onLiveQuestionAnswered
                ? () => onLiveQuestionAnswered()
                : undefined
            }
            manageAIDrawerContext={false}
            initialSeekSeconds={startSecond}
            studySetId={studySetId}
            onPlaybackProgress={onPlaybackProgress}
            onEngaged={() => reportVideoAnchor(video, onAnchorChange)}
            controlRef={playerControlRef}
            onLiveQuestionOpenChange={onLiveQuestionOpenChange}
          />
        </Suspense>
      </div>
    );
  }

  if (status === 'error') {
    // Last-resort fallback: let the user open the video viewer modal on click.
    return (
      <ChatVideoCard
        title={title}
        thumbnailUrl={thumbnailUrl}
        description={description}
        onClick={() => {
          const fallback = normalizeVideoHit(item);
          if (!fallback) return;
          hapticsImpactLight();
          openVideoViewer(fallback, { startSecond, studySetId });
        }}
      />
    );
  }

  return (
    <ChatVideoCard
      title={title}
      thumbnailUrl={thumbnailUrl}
      description={description}
      disabled
      isOpening
    />
  );
};

export const VideoBlockBody = ({
  item,
  videoDisplay = 'inline',
  startSecond,
  studySetId,
  onPlaybackProgress,
  onAnchorChange,
  playerControlRef,
  onLiveQuestionOpenChange,
  liveQuestionPanelPlacement,
  onLiveQuestionAnswered,
}: VideoBlockBodyProps) => {
  const { open: openVideoViewer } = useVideoViewerModal();
  const fallbackThumbnail = useVideoThumbnail();
  const [isOpening, setIsOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!item) return <div>No more new videos in this turn.</div>;

  const title = getString(item.title) || 'Untitled video';
  const id = getString(item.id);
  const thumbnailUrl = getString(item.thumbnailUrl) || fallbackThumbnail;
  const description = getString(item.description);

  // Main AI chat plays the video inline; the drawer / doc-viewer chat keep the
  // compact card that opens the video viewer modal.
  if (videoDisplay === 'inline') {
    return (
      <InlineChatVideo
        item={item}
        id={id}
        title={title}
        thumbnailUrl={thumbnailUrl}
        description={description}
        startSecond={startSecond}
        studySetId={studySetId}
        onPlaybackProgress={onPlaybackProgress}
        onAnchorChange={onAnchorChange}
        playerControlRef={playerControlRef}
        onLiveQuestionOpenChange={onLiveQuestionOpenChange}
        liveQuestionPanelPlacement={liveQuestionPanelPlacement}
        onLiveQuestionAnswered={onLiveQuestionAnswered}
      />
    );
  }

  const fallbackVideo = normalizeVideoHit(item);

  const openVideo = async () => {
    if (isOpening) return;

    setIsOpening(true);
    setError(null);

    try {
      const video = id ? await getVideoById(id) : fallbackVideo;

      if (!video) {
        setError('No playable video was available for this result.');
        return;
      }

      hapticsImpactLight();
      // Opening the video from the drawer makes it the chat's active anchor.
      reportVideoAnchor(video, onAnchorChange);
      // The card branch seeks too: a video served "at the right start time"
      // must land there whichever surface the student opened it from.
      openVideoViewer(video, { startSecond, studySetId });
    } catch (openError) {
      if (fallbackVideo) {
        hapticsImpactLight();
        reportVideoAnchor(fallbackVideo, onAnchorChange);
        openVideoViewer(fallbackVideo, { startSecond, studySetId });
      } else {
        setError(
          openError instanceof Error
            ? openError.message
            : 'Unable to open this video.'
        );
      }
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <ChatVideoCard
      title={title}
      thumbnailUrl={thumbnailUrl}
      description={description}
      disabled={isOpening}
      isOpening={isOpening}
      error={error}
      errorId={id}
      onClick={() => void openVideo()}
    />
  );
};
